import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button } from '@repo/design-system-kit';
import { useSegment } from '@repo/poly-hooks/segment';
import { useTrackRender } from '@repo/shared-kit/hooks';
import { useQueryClient } from '@tanstack/react-query';
import {
  type ApiDocType,
  type ApiDocument,
  registerDocumentQueries,
} from '@repo/api-kit/queries/register-documents';
import { arrayIncludesString } from '@repo/shared-kit/utils';
import { ITALIAN_ASSOCIATION_CREDITSAFE_CODES } from '../../../organizations';
import { useSuspenseDocuments } from '../../../documents';
import { DocumentUploader, type UploadResult } from '../document-uploader';
import styles from './registration-documents.module.css';

const TRACKING_EVENT = 'onboarding_proof_registration_documents_displayed';

interface OrganizationDocuments {
  documentIds?: readonly string[] | null;
  legalFormCreditsafeCode?: string | null;
}

interface RegistrationDocumentsProps {
  isSubmitting?: boolean;
  onContinue: () => void | Promise<void>;
  organization: OrganizationDocuments;
  organizationId: string;
}

type DocumentSlotKey = 'statuto' | 'atto-costitutivo' | 'fiscal-code' | 'assembly-minutes';

interface DocumentSlot {
  docType: ApiDocType;
  key: DocumentSlotKey;
  required: boolean;
}

const DOCUMENT_SLOTS: DocumentSlot[] = [
  {
    docType: 'bylaw',
    key: 'statuto',
    required: true,
  },
  {
    docType: 'kbis',
    key: 'atto-costitutivo',
    required: true,
  },
  {
    docType: 'fiscal_code_certificate',
    key: 'fiscal-code',
    required: true,
  },
  {
    docType: 'assembly_minutes',
    key: 'assembly-minutes',
    required: false,
  },
];

// Translation keys are referenced as literals so `ember-intl-analyzer` can detect them.
// Other locales than `en` will be added later via Lokalise — react-intl falls back to
// the `en` message until translations land.
function DocumentSlotLabel({ slotKey }: { slotKey: DocumentSlotKey }): React.ReactNode {
  switch (slotKey) {
    case 'statuto':
      return (
        <FormattedMessage id="customer-registration-kit.registration-documents.statuto.label" />
      );
    case 'atto-costitutivo':
      return (
        <FormattedMessage id="customer-registration-kit.registration-documents.atto-costitutivo.label" />
      );
    case 'fiscal-code':
      return (
        <FormattedMessage id="customer-registration-kit.registration-documents.fiscal-code.label" />
      );
    case 'assembly-minutes':
      return (
        <FormattedMessage id="customer-registration-kit.registration-documents.assembly-minutes.label" />
      );
  }
}

const REQUIRED_DOC_TYPES = DOCUMENT_SLOTS.filter(slot => slot.required).map(slot => slot.docType);

function getDocumentsByType(documents: ApiDocument[]): Map<ApiDocType, ApiDocument[]> {
  const documentsByType = new Map<ApiDocType, ApiDocument[]>();

  for (const document of documents) {
    documentsByType.set(document.docType, [
      ...(documentsByType.get(document.docType) ?? []),
      document,
    ]);
  }

  return documentsByType;
}

export function RegistrationDocuments({
  isSubmitting = false,
  onContinue,
  organization,
  organizationId,
}: RegistrationDocumentsProps): React.ReactNode {
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const segment = useSegment();
  const { documents } = useSuspenseDocuments([...(organization.documentIds ?? [])]);
  const [missingDocTypes, setMissingDocTypes] = useState<Set<ApiDocType>>(new Set());

  useTrackRender({ eventName: TRACKING_EVENT });

  const documentsByType = useMemo(() => getDocumentsByType(documents), [documents]);
  const documentSlots = arrayIncludesString(
    ITALIAN_ASSOCIATION_CREDITSAFE_CODES,
    organization.legalFormCreditsafeCode
  )
    ? DOCUMENT_SLOTS
    : DOCUMENT_SLOTS.filter(slot => slot.key !== 'assembly-minutes');

  function handleUploadComplete(slot: DocumentSlot, results: UploadResult[]): void {
    for (const result of results) {
      if (result.status === 'fulfilled') {
        queryClient.setQueryData(
          registerDocumentQueries.item(result.document.id).queryKey,
          result.document
        );
      }
    }

    if (results.some(result => result.status === 'fulfilled')) {
      setMissingDocTypes(current => {
        const next = new Set(current);
        next.delete(slot.docType);
        return next;
      });
      segment.track(`${TRACKING_EVENT}_upload_succeeded`, { doc_type: slot.docType });
    }
  }

  function handleUploadError(slot: DocumentSlot): void {
    segment.track(`${TRACKING_EVENT}_upload_failed`, { doc_type: slot.docType });
  }

  function handleDocumentRemove(document: ApiDocument): void {
    queryClient.removeQueries({ queryKey: registerDocumentQueries.item(document.id).queryKey });
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    segment.track(`${TRACKING_EVENT}_continue_clicked`);

    const missingRequiredDocTypes = REQUIRED_DOC_TYPES.filter(
      docType => !documentsByType.has(docType)
    );

    if (missingRequiredDocTypes.length > 0) {
      setMissingDocTypes(new Set(missingRequiredDocTypes));
      return;
    }

    await onContinue();
  }

  return (
    <form className={styles.wrapper} onSubmit={handleSubmit}>
      <div className={styles.header}>
        <h1 className="heading-section">
          <FormattedMessage id="customer-registration-kit.registration-documents.title" />
        </h1>
        <p className="body-standard">
          <FormattedMessage id="customer-registration-kit.registration-documents.subtitle" />
        </p>
      </div>

      <div className={styles.uploaders}>
        {documentSlots.map(slot => {
          const slotDocuments = documentsByType.get(slot.docType) ?? [];
          const hasMissingError = missingDocTypes.has(slot.docType);

          return (
            <DocumentUploader
              className={styles.uploader}
              docType={slot.docType}
              documents={slotDocuments}
              errorMessage={
                hasMissingError
                  ? formatMessage({
                      id: 'customer-registration-kit.generic.required-field.error',
                    })
                  : undefined
              }
              key={slot.key}
              label={<DocumentSlotLabel slotKey={slot.key} />}
              maxFiles={1}
              onDocumentRemove={handleDocumentRemove}
              onUploadComplete={results => {
                handleUploadComplete(slot, results);
              }}
              onUploadError={() => {
                handleUploadError(slot);
              }}
              subjectId={organizationId}
              subjectType="Organization"
            />
          );
        })}
      </div>

      <Button className={styles.continueButton} isPending={isSubmitting} type="submit">
        <FormattedMessage id="customer-registration-kit.generic.continue.cta" />
      </Button>
    </form>
  );
}
